/**
 * Host Global - Admin/Super Admin Closure Requests Management
 * View and manage property closure requests from property owners
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import LinearProgress from "@mui/material/LinearProgress";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function ClosureRequestsManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [tabValue, setTabValue] = useState(0); // 0 = Pending, 1 = All
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [adminNotes, setAdminNotes] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [tabValue]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const filters = tabValue === 0 ? { status: "Pending" } : {};
      const response = await superAdminAPI.getAllClosureRequests(filters);
      setRequests(response.requests || []);
    } catch (error) {
      console.error("Error fetching closure requests:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load closure requests",
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics
  const analytics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentYear = today.getFullYear();
    const yearStart = new Date(currentYear, 0, 1);
    const yearEnd = new Date(currentYear, 11, 31, 23, 59, 59);

    const allRequests = requests;
    const pendingRequests = allRequests.filter((r) => r.status === "Pending");
    const approvedRequests = allRequests.filter((r) => r.status === "Approved");
    const rejectedRequests = allRequests.filter((r) => r.status === "Rejected");
    const cancelledRequests = allRequests.filter((r) => r.status === "Cancelled");

    // Calculate total closure days used this year (from approved requests)
    let totalClosureDaysThisYear = 0;
    let totalClosureDaysInPackageThisYear = 0;
    let currentlyClosedProperties = new Set();

    approvedRequests.forEach((request) => {
      const startDate = new Date(request.startDate);
      const endDate = new Date(request.endDate);

      // Check if this request is in the current year
      if (startDate <= yearEnd && endDate >= yearStart) {
        const effectiveStart = startDate < yearStart ? yearStart : startDate;
        const effectiveEnd = endDate > yearEnd ? yearEnd : endDate;

        if (effectiveStart <= effectiveEnd) {
          const days = Math.ceil((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
          totalClosureDaysThisYear += days;
        }
      }

      // Check if currently active (today is within closure period)
      if (startDate <= today && endDate >= today) {
        currentlyClosedProperties.add(request.property?._id || request.property);
      }

      // Get package days from validation results
      if (request.validationResults?.totalClosureDaysInPackageThisYear) {
        totalClosureDaysInPackageThisYear = Math.max(
          totalClosureDaysInPackageThisYear,
          request.validationResults.totalClosureDaysInPackageThisYear
        );
      }
    });

    // Calculate approval rate
    const totalReviewed = approvedRequests.length + rejectedRequests.length;
    const approvalRate =
      totalReviewed > 0 ? Math.round((approvedRequests.length / totalReviewed) * 100) : 0;

    return {
      totalRequests: allRequests.length,
      pendingRequests: pendingRequests.length,
      approvedRequests: approvedRequests.length,
      rejectedRequests: rejectedRequests.length,
      cancelledRequests: cancelledRequests.length,
      totalClosureDaysThisYear,
      totalClosureDaysInPackageThisYear,
      currentlyClosedProperties: currentlyClosedProperties.size,
      approvalRate,
      maxDaysPerYear: 20,
      maxDaysInPackagePerYear: 5,
    };
  }, [requests]);

  const handleViewDetails = async (request) => {
    try {
      const response = await superAdminAPI.getClosureRequestById(request._id);
      setSelectedRequest(response.request);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching closure request details:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load closure request details",
      });
    }
  };

  const handleOpenActionDialog = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setAdminNotes("");
    setMessage({ type: "", text: "" });
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    if (!submitting) {
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionType(null);
      setAdminNotes("");
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmitAction = async () => {
    if (actionType === "reject" && (!adminNotes || !adminNotes.trim())) {
      setMessage({ type: "error", text: "Admin notes are required when rejecting a request" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      if (actionType === "approve") {
        await superAdminAPI.approveClosureRequest(selectedRequest._id, adminNotes);
        setMessage({ type: "success", text: "Closure request approved successfully" });
      } else {
        await superAdminAPI.rejectClosureRequest(selectedRequest._id, adminNotes);
        setMessage({ type: "success", text: "Closure request rejected successfully" });
      }

      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionType(null);
      setAdminNotes("");

      await fetchRequests();

      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error(
        `Error ${actionType === "approve" ? "approving" : "rejecting"} closure request:`,
        error
      );
      setMessage({
        type: "error",
        text:
          error.message ||
          `Failed to ${actionType === "approve" ? "approve" : "reject"} closure request`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      case "Cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return "schedule";
      case "Approved":
        return "check_circle";
      case "Rejected":
        return "cancel";
      case "Cancelled":
        return "block";
      default:
        return "help";
    }
  };

  const getValidationMessage = (request) => {
    const validation = request.validationResults || {};
    const messages = [];

    if (validation.exceedsMaxDaysPerYear) {
      messages.push(
        `Exceeds max days per year (${validation.maxDaysPerYear} days). Current: ${validation.totalClosureDaysThisYear} days`
      );
    }
    if (validation.exceedsMaxDaysInPackage) {
      messages.push(
        `Exceeds max days within package dates per year (${
          validation.maxDaysInPackagePerYear || 5
        } days). Current within package: ${validation.totalClosureDaysInPackageThisYear || 0} days`
      );
    }
    if (validation.hasBookingConflicts) {
      messages.push(
        `Has booking conflicts (${validation.conflictingReservations?.length || 0} reservation(s))`
      );
    }

    return messages;
  };

  const isCurrentlyActive = (request) => {
    if (request.status !== "Approved") return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(request.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(request.endDate);
    end.setHours(0, 0, 0, 0);
    return start <= today && end >= today;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const pendingRequests = requests.filter((r) => r.status === "Pending");
  const allRequests = requests;
  const displayRequests = tabValue === 0 ? pendingRequests : allRequests;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold" mb={0.5}>
            Property Closure Requests
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Review and manage property closure requests from property owners
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Analytics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="warning"
              icon="pending_actions"
              title="Pending Requests"
              count={analytics.pendingRequests}
              percentage={{
                color: "warning",
                amount: `${
                  analytics.totalRequests > 0
                    ? Math.round((analytics.pendingRequests / analytics.totalRequests) * 100)
                    : 0
                }%`,
                label: "of total requests",
              }}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="success"
              icon="check_circle"
              title="Approved Requests"
              count={analytics.approvedRequests}
              percentage={{
                color: "success",
                amount: `${analytics.approvalRate}%`,
                label: "approval rate",
              }}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="info"
              icon="event_busy"
              title="Total Closure Days"
              count={analytics.totalClosureDaysThisYear}
              percentage={{
                color:
                  analytics.totalClosureDaysThisYear > analytics.maxDaysPerYear
                    ? "error"
                    : "success",
                amount: `${analytics.maxDaysPerYear} days`,
                label: "max per year",
              }}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComplexStatisticsCard
              color="dark"
              icon="home"
              title="Currently Closed"
              count={analytics.currentlyClosedProperties}
              percentage={{
                color: "info",
                amount: `${analytics.approvedRequests} approved`,
                label: "total closures",
              }}
              sx={{ height: "100%", minHeight: "180px" }}
            />
          </Grid>
        </Grid>

        {/* Additional Analytics Row */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: "info.main",
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    <Icon>calendar_today</Icon>
                  </Avatar>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                      Closure Days Usage
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Year {new Date().getFullYear()} Summary
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="body2" fontWeight="medium">
                      Total Closure Days
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold" color="info.main">
                      {analytics.totalClosureDaysThisYear} / {analytics.maxDaysPerYear}
                    </MDTypography>
                  </MDBox>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      (analytics.totalClosureDaysThisYear / analytics.maxDaysPerYear) * 100,
                      100
                    )}
                    color={
                      analytics.totalClosureDaysThisYear > analytics.maxDaysPerYear
                        ? "error"
                        : "info"
                    }
                    sx={{ height: 8, borderRadius: 2, mb: 2 }}
                  />
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="body2" fontWeight="medium">
                      Package Dates Closure Days
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold" color="success.main">
                      {analytics.totalClosureDaysInPackageThisYear} /{" "}
                      {analytics.maxDaysInPackagePerYear}
                    </MDTypography>
                  </MDBox>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      (analytics.totalClosureDaysInPackageThisYear /
                        analytics.maxDaysInPackagePerYear) *
                        100,
                      100
                    )}
                    color={
                      analytics.totalClosureDaysInPackageThisYear >
                      analytics.maxDaysInPackagePerYear
                        ? "error"
                        : "success"
                    }
                    sx={{ height: 8, borderRadius: 2 }}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    <Icon>analytics</Icon>
                  </Avatar>
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="bold">
                      Request Statistics
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Overall Performance
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <MDBox textAlign="center" p={2} bgcolor="grey.100" borderRadius={2}>
                      <MDTypography variant="h4" fontWeight="bold" color="text">
                        {analytics.totalRequests}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        Total Requests
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6}>
                    <MDBox textAlign="center" p={2} bgcolor="error.light" borderRadius={2}>
                      <MDTypography variant="h4" fontWeight="bold" color="error.main">
                        {analytics.rejectedRequests}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        Rejected
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6}>
                    <MDBox textAlign="center" p={2} bgcolor="success.light" borderRadius={2}>
                      <MDTypography variant="h4" fontWeight="bold" color="success.main">
                        {analytics.approvedRequests}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        Approved
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6}>
                    <MDBox textAlign="center" p={2} bgcolor="grey.100" borderRadius={2}>
                      <MDTypography variant="h4" fontWeight="bold" color="text">
                        {analytics.cancelledRequests}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        Cancelled
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3, overflow: "hidden" }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "grey.50",
              "& .MuiTab-root": {
                minHeight: 64,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                px: 3,
              },
            }}
          >
            <Tab
              label={
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <Icon>pending</Icon>
                  <MDTypography variant="button" fontWeight="bold">
                    Pending
                  </MDTypography>
                  {pendingRequests.length > 0 && (
                    <Chip
                      label={pendingRequests.length}
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 700, minWidth: 28 }}
                    />
                  )}
                </MDBox>
              }
            />
            <Tab
              label={
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <Icon>list</Icon>
                  <MDTypography variant="button" fontWeight="bold">
                    All Requests
                  </MDTypography>
                  {allRequests.length > 0 && (
                    <Chip
                      label={allRequests.length}
                      size="small"
                      color="info"
                      sx={{ fontWeight: 700, minWidth: 28 }}
                    />
                  )}
                </MDBox>
              }
            />
          </Tabs>
        </Card>

        {/* Requests Table */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: "hidden" }}>
          <MDBox p={3}>
            {displayRequests.length === 0 ? (
              <MDBox textAlign="center" py={8}>
                <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>event_busy</Icon>
                <MDTypography variant="h6" color="text.secondary" mb={1}>
                  No {tabValue === 0 ? "Pending" : ""} Closure Requests
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {tabValue === 0
                    ? "There are no pending closure requests to review."
                    : "No closure requests have been submitted yet."}
                </MDTypography>
              </MDBox>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }}>Property</TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }}>Property Owner</TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }}>Closure Period</TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }} align="center">
                        Days
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }} align="center">
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }} align="center">
                        Validation
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", py: 2 }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayRequests.map((request) => {
                      const validationMessages = getValidationMessage(request);
                      const hasViolations = validationMessages.length > 0;
                      const isActive = isCurrentlyActive(request);

                      return (
                        <TableRow
                          key={request._id}
                          hover
                          sx={{
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                            ...(isActive && {
                              bgcolor: "success.light",
                              "&:hover": {
                                bgcolor: "success.light",
                              },
                            }),
                          }}
                        >
                          <TableCell>
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                sx={{
                                  bgcolor: "info.main",
                                  width: 40,
                                  height: 40,
                                  fontSize: "0.875rem",
                                }}
                              >
                                <Icon fontSize="small">home</Icon>
                              </Avatar>
                              <MDBox>
                                <MDTypography variant="body2" fontWeight="medium">
                                  {request.property?.title?.en || request.property?.title || "N/A"}
                                </MDTypography>
                                {request.property?.shortId && (
                                  <MDTypography variant="caption" color="text.secondary">
                                    ID: {request.property.shortId}
                                  </MDTypography>
                                )}
                                {isActive && (
                                  <Chip
                                    label="Currently Closed"
                                    size="small"
                                    color="success"
                                    sx={{ mt: 0.5, fontSize: "0.65rem", height: 20 }}
                                  />
                                )}
                              </MDBox>
                            </MDBox>
                          </TableCell>
                          <TableCell>
                            <MDBox>
                              <MDTypography variant="body2" fontWeight="medium">
                                {request.propertyOwner?.fullName || "N/A"}
                              </MDTypography>
                              {request.propertyOwner?.email && (
                                <MDTypography variant="caption" color="text.secondary">
                                  {request.propertyOwner.email}
                                </MDTypography>
                              )}
                            </MDBox>
                          </TableCell>
                          <TableCell>
                            <MDBox>
                              <MDTypography variant="body2" fontWeight="medium">
                                {formatDate(request.startDate)}
                              </MDTypography>
                              <MDTypography variant="caption" color="text.secondary">
                                to {formatDate(request.endDate)}
                              </MDTypography>
                            </MDBox>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={request.numberOfDays}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={<Icon fontSize="small">{getStatusIcon(request.status)}</Icon>}
                              label={request.status}
                              color={getStatusColor(request.status)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {hasViolations ? (
                              <Tooltip title={validationMessages.join(", ")} arrow>
                                <Chip
                                  label="Violations"
                                  color="error"
                                  size="small"
                                  icon={<Icon fontSize="small">warning</Icon>}
                                  sx={{ fontWeight: 600 }}
                                />
                              </Tooltip>
                            ) : request.status === "Pending" ? (
                              <Chip
                                label="Valid"
                                color="success"
                                size="small"
                                icon={<Icon fontSize="small">check_circle</Icon>}
                                sx={{ fontWeight: 600 }}
                              />
                            ) : (
                              <MDTypography variant="caption" color="text.secondary">
                                —
                              </MDTypography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <MDBox display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title="View Full Details" arrow>
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleViewDetails(request)}
                                  sx={{
                                    "&:hover": {
                                      bgcolor: "info.light",
                                      color: "info.main",
                                    },
                                  }}
                                >
                                  <Icon fontSize="small">visibility</Icon>
                                </IconButton>
                              </Tooltip>
                              {request.status === "Pending" && (
                                <>
                                  <Tooltip
                                    title={
                                      hasViolations
                                        ? "Cannot approve - has violations"
                                        : "Approve Request"
                                    }
                                    arrow
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleOpenActionDialog(request, "approve")}
                                        disabled={hasViolations}
                                        sx={{
                                          "&:hover": {
                                            bgcolor: "success.light",
                                            color: "success.main",
                                          },
                                          "&.Mui-disabled": {
                                            bgcolor: "grey.200",
                                          },
                                        }}
                                      >
                                        <Icon fontSize="small">check_circle</Icon>
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Reject Request" arrow>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleOpenActionDialog(request, "reject")}
                                      sx={{
                                        "&:hover": {
                                          bgcolor: "error.light",
                                          color: "error.main",
                                        },
                                      }}
                                    >
                                      <Icon fontSize="small">cancel</Icon>
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </MDBox>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Enhanced Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            py: 2.5,
            px: 3,
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                <Icon sx={{ fontSize: "1.75rem" }}>info</Icon>
              </Avatar>
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                  Closure Request Details
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                  Request ID: {selectedRequest?._id?.slice(-8) || "N/A"}
                </MDTypography>
              </MDBox>
            </MDBox>
            {selectedRequest && (
              <Chip
                icon={<Icon fontSize="small">{getStatusIcon(selectedRequest.status)}</Icon>}
                label={selectedRequest.status}
                color={getStatusColor(selectedRequest.status)}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            )}
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "grey.50" }}>
          {selectedRequest && (
            <MDBox>
              {/* Main Information Cards */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Avatar sx={{ bgcolor: "info.main", width: 32, height: 32 }}>
                        <Icon fontSize="small">home</Icon>
                      </Avatar>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Property
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body1" fontWeight="bold">
                      {selectedRequest.property?.title?.en ||
                        selectedRequest.property?.title ||
                        "N/A"}
                    </MDTypography>
                    {selectedRequest.property?.shortId && (
                      <MDTypography variant="caption" color="text.secondary">
                        ID: {selectedRequest.property.shortId}
                      </MDTypography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
                        <Icon fontSize="small">person</Icon>
                      </Avatar>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Property Owner
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body1" fontWeight="bold">
                      {selectedRequest.propertyOwner?.fullName || "N/A"}
                    </MDTypography>
                    {selectedRequest.propertyOwner?.email && (
                      <MDTypography variant="caption" color="text.secondary">
                        {selectedRequest.propertyOwner.email}
                      </MDTypography>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Avatar sx={{ bgcolor: "success.main", width: 32, height: 32 }}>
                        <Icon fontSize="small">event</Icon>
                      </Avatar>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Start Date
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body1" fontWeight="bold">
                      {formatDate(selectedRequest.startDate)}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Avatar sx={{ bgcolor: "error.main", width: 32, height: 32 }}>
                        <Icon fontSize="small">event_busy</Icon>
                      </Avatar>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        End Date
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body1" fontWeight="bold">
                      {formatDate(selectedRequest.endDate)}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                      <Avatar sx={{ bgcolor: "warning.main", width: 32, height: 32 }}>
                        <Icon fontSize="small">calendar_today</Icon>
                      </Avatar>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
                        Duration
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="bold" color="info.main">
                      {selectedRequest.numberOfDays} days
                    </MDTypography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Reason */}
              {selectedRequest.reason && (
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2, mb: 2 }}>
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    <Avatar sx={{ bgcolor: "secondary.main", width: 32, height: 32 }}>
                      <Icon fontSize="small">note</Icon>
                    </Avatar>
                    <MDTypography variant="subtitle2" fontWeight="bold">
                      Reason for Closure
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="text.secondary">
                    {selectedRequest.reason}
                  </MDTypography>
                </Paper>
              )}

              {/* Admin Notes */}
              {selectedRequest.adminNotes && (
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: "warning.light", borderRadius: 2, mb: 2 }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    <Avatar sx={{ bgcolor: "warning.main", width: 32, height: 32 }}>
                      <Icon fontSize="small">admin_panel_settings</Icon>
                    </Avatar>
                    <MDTypography variant="subtitle2" fontWeight="bold">
                      Admin Notes
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2">{selectedRequest.adminNotes}</MDTypography>
                  {selectedRequest.reviewedBy && (
                    <MDTypography variant="caption" color="text.secondary" mt={1} display="block">
                      Reviewed by: {selectedRequest.reviewedBy?.fullName || "N/A"} on{" "}
                      {formatDateTime(selectedRequest.reviewedAt)}
                    </MDTypography>
                  )}
                </Paper>
              )}

              {/* Validation Results */}
              {selectedRequest.validationResults && (
                <Paper elevation={0} sx={{ p: 3, bgcolor: "white", borderRadius: 2, mb: 2 }}>
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Avatar sx={{ bgcolor: "info.main", width: 40, height: 40 }}>
                      <Icon>verified</Icon>
                    </Avatar>
                    <MDTypography variant="h6" fontWeight="bold">
                      Validation Results
                    </MDTypography>
                  </MDBox>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                      <MDBox p={1.5} bgcolor="grey.50" borderRadius={1}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Max Days Per Year
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">
                          {selectedRequest.validationResults.maxDaysPerYear || 20} days
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox p={1.5} bgcolor="grey.50" borderRadius={1}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Total Closure Days This Year
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold" color="info.main">
                          {selectedRequest.validationResults.totalClosureDaysThisYear || 0} days
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox p={1.5} bgcolor="grey.50" borderRadius={1}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Max Days in Package Per Year
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">
                          {selectedRequest.validationResults.maxDaysInPackagePerYear || 5} days
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox p={1.5} bgcolor="grey.50" borderRadius={1}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Total Package Days Used
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold" color="success.main">
                          {selectedRequest.validationResults.totalClosureDaysInPackageThisYear || 0}{" "}
                          days
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>

                  {selectedRequest.validationResults.packageDates &&
                    selectedRequest.validationResults.packageDates.length > 0 && (
                      <MDBox mt={2} p={2} bgcolor="info.light" borderRadius={2}>
                        <MDTypography variant="body2" fontWeight="bold" mb={1}>
                          Package Allowed Dates (
                          {selectedRequest.validationResults.packageDates.length} dates)
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          {selectedRequest.validationResults.packageDates
                            .slice(0, 10)
                            .map((date, idx) => formatDate(date))
                            .join(", ")}
                          {selectedRequest.validationResults.packageDates.length > 10 &&
                            ` ... and ${
                              selectedRequest.validationResults.packageDates.length - 10
                            } more`}
                        </MDTypography>
                      </MDBox>
                    )}

                  {getValidationMessage(selectedRequest).length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <MDTypography variant="body2" fontWeight="bold" mb={1}>
                        Constraint Violations:
                      </MDTypography>
                      {getValidationMessage(selectedRequest).map((msg, idx) => (
                        <MDTypography key={idx} variant="body2" component="div">
                          • {msg}
                        </MDTypography>
                      ))}
                    </Alert>
                  )}

                  {selectedRequest.validationResults.hasBookingConflicts &&
                    selectedRequest.validationResults.conflictingReservations &&
                    selectedRequest.validationResults.conflictingReservations.length > 0 && (
                      <MDBox mt={2}>
                        <MDTypography variant="body2" fontWeight="bold" mb={1}>
                          Conflicting Reservations:
                        </MDTypography>
                        {selectedRequest.validationResults.conflictingReservations.map(
                          (reservation, idx) => (
                            <MDBox key={idx} mb={1} p={1.5} bgcolor="error.light" borderRadius={1}>
                              <MDTypography variant="body2" fontWeight="medium">
                                Reservation #
                                {reservation.reservationNumber || reservation._id?.slice(-6)}
                              </MDTypography>
                              <MDTypography variant="caption" color="text.secondary">
                                {formatDate(reservation.checkInDate)} to{" "}
                                {formatDate(reservation.checkOutDate)}
                              </MDTypography>
                              {reservation.client && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mt={0.5}
                                >
                                  Client: {reservation.client.fullName || reservation.client.email}
                                </MDTypography>
                              )}
                            </MDBox>
                          )
                        )}
                      </MDBox>
                    )}

                  {getValidationMessage(selectedRequest).length === 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <MDTypography variant="body2" fontWeight="bold">
                        ✓ All constraints are satisfied. This request can be approved.
                      </MDTypography>
                    </Alert>
                  )}
                </Paper>
              )}
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: "white", borderTop: 1, borderColor: "divider" }}>
          <MDButton
            onClick={() => setDetailsDialogOpen(false)}
            color="secondary"
            variant="outlined"
            sx={{ minHeight: "44px", px: 3 }}
          >
            Close
          </MDButton>
          {selectedRequest?.status === "Pending" && (
            <>
              <MDButton
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleOpenActionDialog(selectedRequest, "approve");
                }}
                color="success"
                variant="gradient"
                disabled={getValidationMessage(selectedRequest).length > 0}
                startIcon={<Icon>check_circle</Icon>}
                sx={{ minHeight: "44px", px: 4 }}
              >
                Approve Request
              </MDButton>
              <MDButton
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleOpenActionDialog(selectedRequest, "reject");
                }}
                color="error"
                variant="gradient"
                startIcon={<Icon>cancel</Icon>}
                sx={{ minHeight: "44px", px: 4 }}
              >
                Reject Request
              </MDButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Enhanced Approve/Reject Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 6,
          },
        }}
      >
        <DialogTitle
          sx={{
            background:
              actionType === "approve"
                ? "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)"
                : "linear-gradient(135deg, #f44336 0%, #e57373 100%)",
            color: "white",
            py: 2.5,
            px: 3,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
              <Icon sx={{ fontSize: "1.75rem" }}>
                {actionType === "approve" ? "check_circle" : "cancel"}
              </Icon>
            </Avatar>
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold">
                {actionType === "approve" ? "Approve" : "Reject"} Closure Request
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.9 }}>
                {actionType === "approve"
                  ? "Confirm approval of this closure request"
                  : "Provide reason for rejection"}
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "grey.50" }}>
          {selectedRequest && (
            <MDBox>
              {/* Request Summary */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: "white", borderRadius: 2, mb: 3 }}>
                <MDTypography variant="subtitle2" fontWeight="bold" mb={1.5}>
                  Request Summary
                </MDTypography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Property
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedRequest.property?.title?.en || "N/A"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Duration
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {selectedRequest.numberOfDays} days
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      Start Date
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {formatDate(selectedRequest.startDate)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6}>
                    <MDTypography variant="caption" color="text.secondary">
                      End Date
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {formatDate(selectedRequest.endDate)}
                    </MDTypography>
                  </Grid>
                </Grid>
              </Paper>

              {actionType === "approve" && getValidationMessage(selectedRequest).length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    Cannot approve: This request violates the following constraints:
                  </MDTypography>
                  {getValidationMessage(selectedRequest).map((msg, idx) => (
                    <MDTypography key={idx} variant="body2" component="div">
                      • {msg}
                    </MDTypography>
                  ))}
                </Alert>
              )}

              <MDTypography variant="body2" color="text.secondary" mb={2}>
                {actionType === "approve"
                  ? "Are you sure you want to approve this closure request? The property will be marked as 'Closed' during the requested period."
                  : "Please provide a detailed reason for rejecting this closure request. This will be visible to the property owner."}
              </MDTypography>

              <TextField
                fullWidth
                multiline
                rows={4}
                label={actionType === "approve" ? "Admin Notes (Optional)" : "Admin Notes *"}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={
                  actionType === "approve"
                    ? "Add any notes about this approval (optional)..."
                    : "Explain why this request is being rejected (required)..."
                }
                required={actionType === "reject"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: "white", borderTop: 1, borderColor: "divider" }}>
          <MDButton
            onClick={handleCloseActionDialog}
            color="secondary"
            variant="outlined"
            disabled={submitting}
            sx={{ minHeight: "44px", px: 3 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitAction}
            color={actionType === "approve" ? "success" : "error"}
            variant="gradient"
            disabled={
              submitting ||
              (actionType === "reject" && (!adminNotes || !adminNotes.trim())) ||
              (actionType === "approve" && getValidationMessage(selectedRequest).length > 0)
            }
            startIcon={
              submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>{actionType === "approve" ? "check_circle" : "cancel"}</Icon>
              )
            }
            sx={{ minHeight: "44px", px: 4 }}
          >
            {submitting
              ? actionType === "approve"
                ? "Approving..."
                : "Rejecting..."
              : actionType === "approve"
              ? "Approve Request"
              : "Reject Request"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default ClosureRequestsManagement;
